import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import ExportExcelButton from '../ExportExcelButton';
import { getLocalDateInputValue, formatRecordDate } from '../../utils/dateUtils';
import { LEAVE_TYPES, LEAVE_TYPE_LABELS } from '../../utils/personnelLabels';
import toast from 'react-hot-toast';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

// Iki tarih arasi gun sayisi (her iki uc dahil).
const daysBetween = (start, end) => {
    if (!start || !end) return 1;
    const s = new Date(start);
    const e = new Date(end);
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 1;
};

const LeavesTab = ({ personnelId }) => {
    const [leaves, setLeaves] = useState([]);
    const [balance, setBalance] = useState(null);
    const [year, setYear] = useState(String(currentYear));
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState({
        type: 'yillik',
        startDate: getLocalDateInputValue(),
        endDate: getLocalDateInputValue(),
        dayCount: 1,
        note: '',
    });
    const [dayCountTouched, setDayCountTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchLeaves = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/personnel/${personnelId}/leaves`, { params: { year } });
            setLeaves(res.data.data || []);
            setBalance(res.data.balance || null);
        } catch (error) {
            toast.error('İzinler yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [personnelId, year]);

    useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

    // Tarih degisince gun sayisini otomatik hesapla (kullanici elle degistirmediyse).
    const updateDates = (field, value) => {
        const next = { ...form, [field]: value };
        if (!dayCountTouched) {
            next.dayCount = daysBetween(next.startDate, next.endDate);
        }
        setForm(next);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (form.endDate < form.startDate) {
            toast.error('Bitiş tarihi başlangıçtan önce olamaz.');
            return;
        }
        if (!form.dayCount || parseFloat(form.dayCount) <= 0) {
            toast.error('Gün sayısı 0’dan büyük olmalı.');
            return;
        }
        setIsSubmitting(true);
        try {
            await api.post(`/personnel/${personnelId}/leaves`, { ...form, dayCount: parseFloat(form.dayCount) });
            toast.success('İzin eklendi.');
            setForm({ type: 'yillik', startDate: getLocalDateInputValue(), endDate: getLocalDateInputValue(), dayCount: 1, note: '' });
            setDayCountTouched(false);
            fetchLeaves();
        } catch (error) {
            toast.error(error.response?.data?.message || 'İzin eklenemedi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu izin kaydını silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/personnel/leaves/${id}`);
            toast.success('İzin silindi.');
            fetchLeaves();
        } catch (error) {
            toast.error('Silinemedi.');
        }
    };

    const excelMapper = (row) => ({
        'Tür': LEAVE_TYPE_LABELS[row.type] || row.type,
        'Başlangıç': formatRecordDate(row.startDate),
        'Bitiş': formatRecordDate(row.endDate),
        'Gün': row.dayCount,
        'Not': row.note || '-',
    });

    const inputClass = 'px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white text-sm';

    return (
        <div className="space-y-5">
            {/* Bakiye kartı */}
            {balance && (
                <div className="flex flex-wrap gap-3">
                    <div className="glass border border-dark-border rounded-xl px-4 py-3 min-w-[120px]">
                        <div className="text-xs text-dark-muted uppercase tracking-wider mb-1">İzin Hakkı ({balance.year})</div>
                        <div className="text-lg font-bold text-dark-text">{balance.entitlement} gün</div>
                    </div>
                    <div className="glass border border-dark-border rounded-xl px-4 py-3 min-w-[120px]">
                        <div className="text-xs text-dark-muted uppercase tracking-wider mb-1">Kullanılan</div>
                        <div className="text-lg font-bold text-orange-400">{balance.used} gün</div>
                    </div>
                    <div className="glass border border-dark-border rounded-xl px-4 py-3 min-w-[120px]">
                        <div className="text-xs text-dark-muted uppercase tracking-wider mb-1">Kalan</div>
                        <div className={`text-lg font-bold ${balance.remaining < 0 ? 'text-red-500' : 'text-green-400'}`}>{balance.remaining} gün</div>
                    </div>
                </div>
            )}

            {/* Ekleme formu */}
            <form onSubmit={handleAdd} className="glass border border-dark-border rounded-xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Tür</label>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputClass}>
                        {LEAVE_TYPES.map((t) => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Başlangıç</label>
                    <input type="date" value={form.startDate} onChange={(e) => updateDates('startDate', e.target.value)} className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Bitiş</label>
                    <input type="date" value={form.endDate} onChange={(e) => updateDates('endDate', e.target.value)} className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Gün</label>
                    <input type="number" step="0.5" value={form.dayCount} onChange={(e) => { setDayCountTouched(true); setForm({ ...form, dayCount: e.target.value }); }} className={inputClass} />
                </div>
                <div className="flex flex-col gap-1 col-span-2 md:col-span-1">
                    <label className="text-xs text-dark-muted">Not</label>
                    <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputClass} placeholder="Opsiyonel" />
                </div>
                <div className="col-span-2 md:col-span-1">
                    <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-1 bg-primary-600 hover:bg-primary-500 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                        <Plus size={16} /> Ekle
                    </button>
                </div>
            </form>

            {/* Filtre + Excel */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <select value={year} onChange={(e) => setYear(e.target.value)} className={inputClass}>
                    {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <ExportExcelButton data={leaves} mapper={excelMapper} sheetName="Izinler" filename="Personel_Izinler.xlsx" label="EXCEL" />
            </div>

            {/* Liste */}
            <div className="glass border border-dark-border rounded-xl overflow-hidden">
                <div className="mobile-table-hint lg:hidden">Tüm izin ayrıntıları için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Tür</th>
                                <th className="px-4 py-3 font-semibold">Başlangıç</th>
                                <th className="px-4 py-3 font-semibold">Bitiş</th>
                                <th className="px-4 py-3 font-semibold">Gün</th>
                                <th className="px-4 py-3 font-semibold">Not</th>
                                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted"><Loader2 className="animate-spin inline" size={20} /></td></tr>
                            ) : leaves.length === 0 ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted">İzin kaydı bulunamadı.</td></tr>
                            ) : (
                                leaves.map((l) => (
                                    <tr key={l.id} className="hover:bg-dark-hover transition-colors">
                                        <td className="px-4 py-3">{LEAVE_TYPE_LABELS[l.type] || l.type}</td>
                                        <td className="px-4 py-3 text-dark-muted">{formatRecordDate(l.startDate)}</td>
                                        <td className="px-4 py-3 text-dark-muted">{formatRecordDate(l.endDate)}</td>
                                        <td className="px-4 py-3 font-semibold">{l.dayCount}</td>
                                        <td className="px-4 py-3 text-dark-muted max-w-[160px] truncate" title={l.note}>{l.note || '-'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => handleDelete(l.id)} className="p-1.5 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LeavesTab;
